import {ListBase, ListProps, useListContext, usePermissions, Record, useDataProvider, useNotify, useQuery} from "react-admin";
import React, { useState } from "react";
import {Table, Button, Space, Card, Switch} from "antd";
import { FileTextOutlined, PlusOutlined } from "@ant-design/icons";
import {Link} from "react-router-dom";
import DeleteButton from "@src/components/Common/DeleteButton";
import { ExtendedDataProvider } from "@src/types";
import {BaseFilterDropdown} from "@src/components/Filters/BaseFilterDropdown";
import SkeletonTable, {SkeletonTableColumnsType} from "@src/components/Common/SkeletonTable";
import PackDashboardTabs from "../PackDashboard/PackDashboardTabs";


const DocumentListView = (props: { service_id: any; }) => {
    const { ids, data, loading, refetch, setFilters, filterValues, page, perPage, setPage, setPerPage, total } = useListContext();
    const { permissions } = usePermissions();
    const { service_id } = props;
    const resource = "serviceowner_pack_documents";
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const [updatingIds, setUpdatingIds] = useState<number[]>([]);

    const handleToggleEnabled = async (record: any) => {
        setUpdatingIds(prev => [...prev, record.id]);
        try {
            await dataProvider.update(resource, {
                id: record.id,
                data: { enabled: !record.enabled },
                previousData: record
            });
            notify(`Document ${record.enabled ? 'disabled' : 'enabled'} successfully`);
            refetch();
        } catch (error: any) {
            notify(`Failed to update document: ${error.message}`, 'error');
        } finally {
            setUpdatingIds(prev => prev.filter(id => id !== record.id));
        }
    };

    const columns = [
        {
            title: 'Filename',
            dataIndex: 'filename',
            key: 'filename',
            width: 1,
            onCell: () => ({
                style: { whiteSpace: 'nowrap' as const }
            }),
            render: (text: string, record: any) => (
                <Space>
                    <FileTextOutlined />
                    <Link to={`/${resource}/${record.id}/edit/`}>{text}</Link>
                </Space>
            ),
        },
        {
            title: 'Content Size',
            dataIndex: 'num_chars',
            key: 'num_chars',
            width: 1,
            onCell: () => ({
                style: { whiteSpace: 'nowrap' as const }
            }),
            render: (size: number) => {
                if (!size && size !== 0) return '-';
                return `${size.toLocaleString()} chars`;
            },
        },
        {
            title: 'Content Preview',
            dataIndex: 'preview',
            key: 'preview',
            ellipsis: true,
        },
        {
            title: 'Service',
            dataIndex: ['service', 'name'],
            key: 'service',
            ellipsis: true,
        },
        {
            key: 'actions',
            render: (text: any, record: any) => (
                permissions === "ADMIN" && (
                    <Space style={{ float: "right" }}>
                        <Switch
                            checked={record.enabled}
                            loading={updatingIds.includes(record.id)}
                            onChange={() => handleToggleEnabled(record)}
                            checkedChildren="Enabled"
                            unCheckedChildren="Disabled"
                        />
                        <DeleteButton size={"small"} title={"Document"} resource={resource} record={record} refetch={refetch}/>
                    </Space>
                )
            ),
        },
    ];

    const tableData: Record[] = []
    ids.forEach(id => {
        tableData.push(data[id])
    });
    return (
        <Card
            className="criclebox tablespace mb-24 pack-card"
        >
            {/* Filters Row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: 0, padding: '8px 16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <BaseFilterDropdown
                    // title="Service"
                    allTitle="All Services"
                    placeholder="Select service"
                    value={filterValues?.service_id}
                    onChange={(value) => setFilters({ ...filterValues, service_id: value }, {})}
                    resource="services"
                />
                {permissions === "ADMIN" && (
                    <div style={{ marginLeft: 'auto' }}>
                        <Link to={`/serviceowner_pack_documents/create?service_id=${service_id}`}>
                            <Button type='primary' icon={<PlusOutlined />}>Add Document</Button>
                        </Link>
                    </div>
                )}
            </div>
            <SkeletonTable loading={loading} columns={columns as SkeletonTableColumnsType[]}>
                <Table
                    dataSource={tableData}
                    columns={columns}
                    rowKey="id"
                    tableLayout="auto"
                    pagination={{
                        current: page,
                        pageSize: perPage,
                        total: total,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} documents`,
                        className: "pagination-padding",
                        onChange: (newPage, newPageSize) => {
                            if (newPageSize !== perPage) {
                                setPerPage(newPageSize);
                                setPage(1);
                            } else {
                                setPage(newPage);
                            }
                        },
                    }}
                />
            </SkeletonTable>
        </Card>
    );
}


const DocumentList = (props: ListProps) => (
    <>
        <PackDashboardTabs />
        <ListBase {...props}>
            <DocumentListView service_id={props?.filter?.service_id}/>
        </ListBase>
    </>
)


export default DocumentList;